import React, { useState, useCallback } from 'react';
import { Container, Graphics } from '@pixi/react';

// Helper to calculate angle and distance
function getAngleAndLength(x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return {
    angle: Math.atan2(dy, dx),
    length: Math.sqrt(dx * dx + dy * dy)
  };
}

const HANDLE_SIZE = 28; // Rectangle hit area size for handles
const VISIBLE_RADIUS = 8; // Visible handle size
const BODY_SIZE = 32; // Rectangle hit area for body handle
const BODY_VISIBLE_RADIUS = 7;
const HIT_PADDING = 20; // Padding for the main arrow hit area

const Arrow = ({ shape, isSelected, onUpdate, onSelect, isBlueprintMode = false }) => {
  const [dragging, setDragging] = useState(null); // null | 'start' | 'end' | 'body'
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Calculate bounding box for the arrow
  const minX = Math.min(shape.x1, shape.x2) - HIT_PADDING;
  const minY = Math.min(shape.y1, shape.y2) - HIT_PADDING;
  const maxX = Math.max(shape.x1, shape.x2) + HIT_PADDING;
  const maxY = Math.max(shape.y1, shape.y2) + HIT_PADDING;
  const hitWidth = maxX - minX;
  const hitHeight = maxY - minY;

  // Drag start for endpoints or body
  const handlePointerDown = useCallback((e, part) => {
    e.stopPropagation();
    onSelect && onSelect();
    const global = e.data.global;
    if (part === 'body') {
      setDragOffset({ x: global.x - shape.x1, y: global.y - shape.y1 });
    }
    setDragging(part);
  }, [shape, onSelect]);

  // Drag move
  const handlePointerMove = useCallback((e) => {
    if (!dragging) return;
    const global = e.data.global;
    if (dragging === 'body') {
      const dx = global.x - dragOffset.x;
      const dy = global.y - dragOffset.y;
      const offsetX = dx - shape.x1;
      const offsetY = dy - shape.y1;
      onUpdate({
        x1: shape.x1 + offsetX,
        y1: shape.y1 + offsetY,
        x2: shape.x2 + offsetX,
        y2: shape.y2 + offsetY
      });
    } else if (dragging === 'start') {
      onUpdate({ x1: global.x, y1: global.y });
    } else if (dragging === 'end') {
      onUpdate({ x2: global.x, y2: global.y });
    }
  }, [dragging, dragOffset, shape, onUpdate]);

  // Drag end
  const handlePointerUp = useCallback(() => {
    setDragging(null);
  }, []);

  // Draw arrow
  const drawArrow = useCallback((g) => {
    if (!shape) return;
    g.clear();
    const { angle } = getAngleAndLength(shape.x1, shape.y1, shape.x2, shape.y2);
    // Main line
    const arrowColor = isBlueprintMode ? 0xffffff : (isSelected ? 0x4a90e2 : 0x222222);
    g.lineStyle(isSelected ? 4 : 2, arrowColor, 1);
    g.moveTo(shape.x1, shape.y1);
    g.lineTo(shape.x2, shape.y2);
    // Arrowhead
    const headlen = 18;
    const theta = Math.PI / 7;
    const angle1 = angle + theta;
    const angle2 = angle - theta;
    g.lineStyle(isSelected ? 4 : 2, arrowColor, 1);
    g.moveTo(shape.x2, shape.y2);
    g.lineTo(shape.x2 - headlen * Math.cos(angle1), shape.y2 - headlen * Math.sin(angle1));
    g.moveTo(shape.x2, shape.y2);
    g.lineTo(shape.x2 - headlen * Math.cos(angle2), shape.y2 - headlen * Math.sin(angle2));
  }, [shape, isSelected]);

  // Draw handles (visible only)
  const drawHandles = useCallback((g) => {
    if (!shape || !isSelected) return;
    g.clear();
    // Start handle
    g.beginFill(0xff6b6b);
    g.drawCircle(shape.x1, shape.y1, VISIBLE_RADIUS);
    g.endFill();
    // End handle
    g.beginFill(0x4ecdc4);
    g.drawCircle(shape.x2, shape.y2, VISIBLE_RADIUS);
    g.endFill();
    // Body handle (center)
    const cx = (shape.x1 + shape.x2) / 2;
    const cy = (shape.y1 + shape.y2) / 2;
    g.beginFill(0x888888);
    g.drawCircle(cx, cy, BODY_VISIBLE_RADIUS);
    g.endFill();
  }, [shape, isSelected]);

  // Draw main hit area rectangle
  const drawHitArea = useCallback((g) => {
    g.clear();
    g.beginFill(0xffffff, 0.1); // transparent white
    g.drawRect(0, 0, hitWidth, hitHeight);
    g.endFill();
  }, [hitWidth, hitHeight]);

  // When clicking the hit area, select the arrow
  const handleHitAreaPointerDown = useCallback((e) => {
    e.stopPropagation();
    onSelect && onSelect();
  }, [onSelect]);

  return (
    <Container
      interactive={true}
      eventMode="static"
      pointermove={dragging ? handlePointerMove : undefined}
      pointerup={handlePointerUp}
      pointerupoutside={handlePointerUp}
    >
      {/* Main hit area rectangle (behind everything) */}
      <Graphics
        x={minX}
        y={minY}
        interactive={true}
        pointerdown={handleHitAreaPointerDown}
        draw={drawHitArea}
      />
      <Graphics draw={drawArrow} />
      {isSelected && (
        <Graphics draw={drawHandles} />
      )}
      {/* Start handle (rectangle hit area) */}
      {isSelected && (
        <Graphics
          interactive={true}
          cursor="grab"
          pointerdown={e => handlePointerDown(e, 'start')}
          x={shape.x1}
          y={shape.y1}
          draw={g => {
            g.clear();
            g.beginFill(0xffffff, 0.1);
            g.drawRect(-HANDLE_SIZE/2, -HANDLE_SIZE/2, HANDLE_SIZE, HANDLE_SIZE);
            g.endFill();
            g.beginFill(0xff6b6b);
            g.drawCircle(0, 0, VISIBLE_RADIUS);
            g.endFill();
          }}
        />
      )}
      {/* End handle (rectangle hit area) */}
      {isSelected && (
        <Graphics
          interactive={true}
          cursor="grab"
          pointerdown={e => handlePointerDown(e, 'end')}
          x={shape.x2}
          y={shape.y2}
          draw={g => {
            g.clear();
            g.beginFill(0xffffff, 0.1);
            g.drawRect(-HANDLE_SIZE/2, -HANDLE_SIZE/2, HANDLE_SIZE, HANDLE_SIZE);
            g.endFill();
            g.beginFill(0x4ecdc4);
            g.drawCircle(0, 0, VISIBLE_RADIUS);
            g.endFill();
          }}
        />
      )}
      {/* Body handle (rectangle hit area) */}
      {isSelected && (
        <Graphics
          interactive={true}
          cursor="move"
          pointerdown={e => handlePointerDown(e, 'body')}
          x={(shape.x1 + shape.x2) / 2}
          y={(shape.y1 + shape.y2) / 2}
          draw={g => {
            g.clear();
            g.beginFill(0xffffff, 0.1);
            g.drawRect(-BODY_SIZE/2, -BODY_SIZE/2, BODY_SIZE, BODY_SIZE);
            g.endFill();
            g.beginFill(0x888888);
            g.drawCircle(0, 0, BODY_VISIBLE_RADIUS);
            g.endFill();
          }}
        />
      )}
    </Container>
  );
};

export default Arrow; 